import React, { forwardRef, useId } from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      fullWidth = false,
      className = '',
      disabled,
      id,
      'aria-describedby': describedBy,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const controlId = id || generatedId;
    const feedbackId = `${controlId}-feedback`;
    const description = [describedBy, (error || helperText) ? feedbackId : undefined].filter(Boolean).join(' ') || undefined;
    const baseStyles = 'field-control';
    const errorStyles = error ? 'border-status-danger focus:ring-status-danger/20 focus:border-status-danger' : 'border-border-default';

    return (
      <div className={`min-w-0 ${fullWidth ? 'w-full' : ''}`}>
        {label && (
          <label htmlFor={controlId} className="field-label">
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="pointer-events-none absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={controlId}
            aria-invalid={error ? true : undefined}
            aria-describedby={description}
            className={`${baseStyles} ${errorStyles} ${
              leftIcon ? 'pl-10' : ''
            } ${rightIcon ? 'pr-10' : ''} ${className}`}
            disabled={disabled}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-muted">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p id={feedbackId} className="mt-1.5 text-xs text-status-danger">{error}</p>
        )}
        {helperText && !error && (
          <p id={feedbackId} className="mt-1.5 text-xs text-text-muted">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
